import React from 'react'
import Flex from 'typescript-styled-flex'
import { WithTranslation, withTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { compose } from 'lodash/fp'
import { Dispatch } from 'redux'
import { isEmpty } from 'lodash'
import { isAfter } from 'date-fns'
import { FormGroup } from '../../../components/FormBuilder'
import Price from '../../../components/Price/Price'
import { IStore } from '../../../reducers'
import getInvoice, { getInvoiceLoading } from '../../../reducers/invoice/getInvoice'
import { IInvoice } from '../../../models/Invoice'
import { createInvoiceFromForm } from '../../../actions/invoices/createInvoice'
import styled, { theme } from '../../../theme'
import reloadInvoice from '../../../actions/invoices/reloadInvoice'
import { isSafari } from '../../../components/DetectBrowser/DetectBrowser'
import { getInvoiceLicense, getLicense, isLicenseValid } from '../../../reducers/userAccount/selectors/license'
import { LicenseType } from '../../../models/License'
import { IWithSize } from '../../BookingCalendar/Calendar/Calendar'
import { withSize } from '../../../hooks/useSize'
import { withIsDesktop } from './withIsDesktop'
import { IntegrationRedirectReason } from '../../../screens/IntegrationAirbnb/IntegrationAirbnb'
import { getUpdateReservationFormEvent } from '../../../reducers/bookingCalendar/selectors/events'
import { ModalActions } from '../../../reducers/modals/actionNames'
import { isUserNewPackage } from '../../../reducers/userAccount/selectors/userAccount'

const Label = styled.div<{ size?: number }>`
  padding: ${({ size }) => (size ? 6 * size : 6)}px 0px;
  text-align: right;
  font-size: ${({ size }) => (size ? 16 * size : 16)}px;
  color: ${() => theme.colors.palette.slateGrey};
`

class InvoiceTab extends React.Component<
  IInvoiceTabProps & IInvoiceTabDispatch & IWithSize
> {
  public constructor(props) {
    super(props)
  }

  public windowReference

  public onClick = () => {
    if (this.props.licenseValid) {
      if (
        this.props.licenseInvoice &&
        this.props.licenseInvoice.link &&
        isAfter(this.props.licenseInvoice.license, new Date())
      ) {
        const { link } = this.props.invoice
        if (link) {
          window.open(link, '_blank')
        } else {
          if (isSafari()) {
            this.windowReference = window.open()
          }
          this.props.createInvoiceFromForm(this.windowReference)
        }
      } else {
        return this.goToInfo(IntegrationRedirectReason.NoSyncing)
      }
    } else {
      return this.goToInfo(IntegrationRedirectReason.NoLicenseValid)
    }
  }

  public reload = () => {
    if (this.props.invoice && this.props.invoice.link) {
      this.props.updateInvoice()
    }
  }

  public openInvoice = () => {
    if (isSafari()) {
      this.windowReference.location = this.props.invoice.link
    } else {
      window.open(this.props.invoice.link, '_blank')
    }
  }

  componentWillMount(): void {
    window.addEventListener('focus', this.reload)
  }

  componentWillUnmount(): void {
    window.removeEventListener('focus', this.reload)
  }

  componentDidUpdate(
    prevProps: Readonly<IInvoiceTabProps & IInvoiceTabDispatch>,
  ): void {
    if (
      (isEmpty(prevProps.invoice) || !prevProps.invoice.link) &&
      this.props.invoice &&
      this.props.invoice.newInvoice
    ) {
      this.openInvoice()
    }
  }

  public goToInfo = (reason: IntegrationRedirectReason) => {
    if ('routerHistory' in window) {
      this.props.toggleHideModal(true)
      ;(window as any).routerHistory.replace({
        pathname: `/invoice-info`,
        search: (window as any).routerHistory.location.search.includes('mobile')
          ? '?mobile=1'
          : '',
        state: { reason, redirect: '/', reservationId: this.props.id || '0' },
      })
    }
  }

  public render(): React.ReactNode {
    const {
      isDesktop,
      invoice,
      t,
      size,
      allowFvat,
      activeLicense,
      newPremium,
      isNewPremiumPriceGroup,
      licenseValid,
    } = this.props
    const isNewPremiumUser = newPremium && isNewPremiumPriceGroup
    const isFreeAccountUser = isNewPremiumPriceGroup && !licenseValid
    const isLiteUser = isNewPremiumUser && activeLicense?.licenseType === LicenseType.LICENSE_PREMIUM_LITE
    const isPlusUser = isNewPremiumUser && activeLicense?.licenseType === LicenseType.LICENSE_PREMIUM_PLUS
    const isProUser = isNewPremiumUser && activeLicense?.licenseType === LicenseType.LICENSE_PREMIUM_PRO

    const isInvoiceEditDisabled = isFreeAccountUser || (isLiteUser && !activeLicense.has_invoices) ||
      (isPlusUser && !activeLicense.has_invoices) ||
      (isProUser && !activeLicense.has_invoices)
    return (
      <Flex
        full={true}
        style={{
          height: 'auto',
          padding: isDesktop
            ? `${10 * size}px ${16 * size}px ${25 * size}px ${16 * size}px`
            : `${10 * size}px ${20 * size}px ${16 * size}px`,
          width: '100%',
        }}
      >
        <Flex full={true}>
          <FormGroup.Render
            size={size}
            isDisabled={isInvoiceEditDisabled}
            withPopoverIcon={isInvoiceEditDisabled}
            withoutBtnPadding={allowFvat}
            withButton={allowFvat}
            loading={this.props.invoiceLoading}
            toggle={this.onClick}
            withEdit={!!invoice.link && allowFvat}
            title={t('z5018_menu_edit_invoice')}
            className={'reservation-form-invoice'}
          >
            {!!invoice.link && (
              <Flex full={true} column={true}>
                <Flex justifyBetween={true}>
                  <Label size={size} style={{ textAlign: 'left' }}>
                    {t('BKO_782_InoviceTabNumber')}
                  </Label>
                  <Label size={size}> {invoice.number}</Label>
                </Flex>
                <Flex justifyBetween={true}>
                  <Label size={size} style={{ textAlign: 'left' }}>
                    {t('BKO_782_InoviceTabNetValue')}
                  </Label>
                  <Label size={size}>
                    <Price value={invoice.price} />
                    &nbsp;
                    {this.props.invoice.currency}
                  </Label>
                </Flex>
                <Flex justifyBetween={true}>
                  <Label size={size} style={{ textAlign: 'left' }}>
                    {t('BKO_782_InoviceTabGrossValue')}
                  </Label>
                  <Label size={size}>
                    <Price value={invoice.priceGross} />
                    &nbsp;
                    {this.props.invoice.currency}
                  </Label>
                </Flex>
                <Flex justifyBetween={true} alignCenter={true}>
                  <Label size={size}>{t('BKO_782_InoviceTabCustomer')}</Label>
                  <Label
                    size={size}
                    style={{
                      maxWidth: '69%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {invoice.buyer}
                  </Label>
                </Flex>
                <Flex justifyBetween={true}>
                  <Label size={size}>{t('BKO_782_InoviceTabCreateDate')}</Label>
                  <Label size={size}>{invoice.date}</Label>
                </Flex>
              </Flex>
            )}
          </FormGroup.Render>
        </Flex>
        {/*Kiedyś ktoś mne zapytał co to jest to na dole, odpowiedziałem, że nie wiem */}
        {isDesktop && (
          <>
            <Flex full={true}></Flex>
            <Flex full={true}></Flex>
          </>
        )}
      </Flex>
    )
  }
}

export interface IInvoiceTabProps extends WithTranslation {
  invoice?: IInvoice
  invoiceLoading?: boolean
  invoiceLicense?: any
  activeLicense?: any
  licenseValid?: any
  licenseInvoice?: any
  isDesktop?: boolean
  newPremium?: boolean
  isNewPremiumPriceGroup?: boolean
  id?: any
  allowFvat?: any
}

export interface IInvoiceTabDispatch {
  createInvoiceFromForm: (reference: any) => void
  updateInvoice: () => void
  toggleHideModal?: any
}

const mapStateToProps = (state: IStore) => {
  const rawEvent = getUpdateReservationFormEvent(state)

  return {
    invoice: getInvoice(state),
    invoiceLicense: getLicense(LicenseType.LICENSE_INVOICE)(state),
    invoiceLoading: getInvoiceLoading(state),
    licenseValid: isLicenseValid(state),
    licenseInvoice: getInvoiceLicense(state),
    newPremium: isUserNewPackage(state),
    isNewPremiumPriceGroup: isUserNewPackage(state),
    id: rawEvent ? rawEvent.id : null,
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  createInvoiceFromForm: reference =>
    dispatch(createInvoiceFromForm(reference)),
  updateInvoice: () => dispatch(reloadInvoice()),
  toggleHideModal: (toggle?: boolean) => {
    dispatch({ type: ModalActions.ToggleHideModal, payload: toggle })
  },
})

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withTranslation(),
  withIsDesktop,
)(withSize(InvoiceTab))
